<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import {
  proceedToLogin,
  proceedToWorkspace,
  reloadTab,
  sessionNotice,
} from "@/utils/session";

/**
 * 탭 세션 안내(30차 S30-01 · S30-02, `utils/session.ts`)
 * - 다른 탭이 공용 토큰을 바꿨을 때(워크스페이스 전환 · 다른 계정 로그인 · 로그아웃): 이 탭의 요청은 모두 막혀 있다. 「새로고침」 으로
 *   바뀐 세션으로 다시 연다.
 * - 이 탭의 세션이 만료됐는데 저장하지 않은 편집이 있을 때: 로그인으로 바로 보내지 않고 머문다. 「로그인으로 이동」 은 돌아올 화면을 싣는다.
 * - 지금 워크스페이스에서 제거됐는데 저장하지 않은 편집이 있을 때(62차 BC62-01): 다른 소속으로 바로 옮기지 않고 머문다. 「○○ 워크스페이스로
 *   전환」 으로 옮긴다.
 * - 90차 C90-03b(AU90-06): 소속이 **하나도 남지 않게** 제거돼 옮겨 갈 워크스페이스가 없으면(`workspaceName` 없음) 「다른 워크스페이스로 전환」 이 아니라 **「로그인으로 이동」** 으로 안내한다 —
 *   설명은 「구성원에서 제거되어 소속된 워크스페이스가 없습니다 … 복사한 뒤 「로그인으로 이동」」(워크스페이스 이름 변수를 쓰지 않는다). 눌림은 `proceedToWorkspace`(대기 중인 전환이 없으면 로그인으로 보낸다).
 * - 모달이 아니라 상단 띠다 — 화면의 입력칸을 그대로 선택 · 복사할 수 있어야 한다. 「화면 입력 내용 복사」 는 지금 화면의 입력칸 ·
 *   글상자 값을 모아 클립보드에 담고, 클립보드를 쓸 수 없으면 모은 글을 띠 안에 펼쳐 직접 복사하게 한다.
 * - 계정 없이 쓰는 화면(로그인 · 회원가입 · 입력 링크)에서는 띄우지 않는다.
 * - 83차 jr-C3(JR83-06): **뜰 때 안내 머리(`tabindex="-1"`)로 포커스를 옮기고, 닫히면 뜨기 직전 자리로 되돌린다.** 이 띠는 `role="alert"` 로 읽히지만 포커스가 그대로라 Tab 이 본문 끝에서부터 가
 *   「화면 입력 내용 복사」 · 「로그인으로 이동」(맨 위)을 손으로 찾아가야 했다. 머리에서 Tab 한 번이 「화면 입력 내용 복사」 다. 띠 종류만 바뀌는 경우(만료 → 워크스페이스 바뀜)는 다시 옮기지 않는다.
 */
const route = useRoute();
const authStore = useAuthStore();

const visible = computed(
  () =>
    sessionNotice.value !== null &&
    !route.matched.some((record) => record.meta.public === true),
);

const kind = computed(() => sessionNotice.value?.kind);

const head = useTemplateRef<HTMLElement>("head");
/** 띠가 뜨기 직전 포커스가 있던 요소 — 닫히면 돌려준다 */
let opener: HTMLElement | null = null;

watch(
  visible,
  async (shown) => {
    if (shown) {
      const active = document.activeElement;
      opener =
        active instanceof HTMLElement && active !== document.body
          ? active
          : null;
      await nextTick();
      head.value?.focus();
      return;
    }
    const target = opener;
    opener = null;
    await nextTick();
    // 닫힌 띠 안에 있던 포커스는 body 로 떨어졌다 — 사용자가 이미 다른 곳을 잡았다면 건드리지 않는다.
    if (
      target?.isConnected &&
      (document.activeElement === document.body ||
        document.activeElement === null)
    )
      target.focus();
  },
  { immediate: true },
);

const title = computed(() => {
  switch (kind.value) {
    case "workspace":
      return "다른 탭에서 워크스페이스가 바뀌었습니다";
    case "account":
      return "다른 탭에서 다른 계정으로 로그인했습니다";
    case "signed-out":
      return "다른 탭에서 로그아웃했습니다";
    case "removed":
      return "이 워크스페이스에서 제거됐습니다";
    default:
      return "세션이 만료됐습니다";
  }
});

const description = computed(() => {
  const workspaceName = authStore.workspace.name || "이 워크스페이스";
  if (kind.value === "removed" && !sessionNotice.value?.workspaceName) {
    return "구성원에서 제거되어 소속된 워크스페이스가 없습니다. 이 화면에서는 더 저장할 수 없으니, 필요한 내용을 복사한 뒤 「로그인으로 이동」 을 눌러 주세요.";
  }
  if (kind.value === "removed") {
    return `${workspaceName} 구성원에서 제거되어 이 화면에서는 더 저장할 수 없습니다. 전환하면 이 화면을 ${sessionNotice.value?.workspaceName ?? "다른"} 워크스페이스 기준으로 다시 열어 저장하지 않은 편집은 사라지니, 필요한 내용을 복사한 뒤 전환해 주세요.`;
  }
  if (kind.value === "expired") {
    return "다시 로그인하면 이 화면으로 돌아오지만 저장하지 않은 편집은 사라집니다. 필요한 내용을 복사한 뒤 「로그인으로 이동」 을 눌러 주세요. 다른 탭에서 같은 계정 · 같은 워크스페이스로 다시 로그인하면 이 화면에서 이어서 저장할 수 있습니다(다른 워크스페이스로 들어가면 워크스페이스가 바뀌었다는 안내로 바뀝니다).";
  }
  return `이 화면은 ${workspaceName} 기준이라, 그대로 저장하면 다른 워크스페이스 · 계정에 저장될 수 있어 이 탭의 조회 · 저장을 멈췄습니다. 새로고침하면 바뀐 세션으로 다시 엽니다. 저장하지 않은 편집은 새로고침하면 사라지니 필요한 내용은 먼저 복사해 주세요.`;
});

/** 클립보드를 쓸 수 없을 때 펼치는 모은 글 */
const fallbackText = ref("");
const fallbackArea = useTemplateRef<HTMLTextAreaElement>("fallbackArea");
const copyMessage = ref("");

watch(sessionNotice, () => {
  fallbackText.value = "";
  copyMessage.value = "";
});

/** 입력칸의 이름(연결된 label · aria-label · placeholder 순) */
function fieldLabel(field: HTMLInputElement | HTMLTextAreaElement): string {
  const labelled = field.labels?.[0]?.innerText.trim();
  return (
    labelled ||
    field.getAttribute("aria-label")?.trim() ||
    field.placeholder.trim() ||
    "입력칸"
  );
}

/** 지금 화면(`.content` 안 화면 영역)의 글 입력칸 값을 "이름: 값" 으로 모은다. 빈 칸은 뺀다. */
function collectFieldText(): string {
  const root = document.querySelector(".content") ?? document.body;
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'textarea, input:not([type]), input[type="text"], input[type="email"], input[type="url"], input[type="number"], input[type="tel"]',
  );
  return [...fields]
    .filter((field) => field.value.trim() !== "")
    .map((field) => `${fieldLabel(field)}: ${field.value}`)
    .join("\n\n");
}

async function copyFields(): Promise<void> {
  const text = collectFieldText();
  if (!text) {
    copyMessage.value = "이 화면에 복사할 입력 내용이 없습니다.";
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    copyMessage.value =
      "화면 입력 내용을 클립보드에 복사했습니다. 메모장 등에 붙여 넣어 두세요.";
  } catch {
    fallbackText.value = text;
    copyMessage.value =
      "클립보드를 쓸 수 없어 아래에 모았습니다. 전체 선택(Ctrl/⌘+A) 뒤 복사해 주세요.";
    await nextTick();
    fallbackArea.value?.select();
  }
}

const primaryLabel = computed(() => {
  if (kind.value === "expired") return "로그인으로 이동";
  if (kind.value === "removed") {
    const next = sessionNotice.value?.workspaceName;
    return next ? `${next} 워크스페이스로 전환` : "로그인으로 이동";
  }
  return "새로고침";
});

function primaryAction(): void {
  if (kind.value === "expired") {
    void proceedToLogin();
  } else if (kind.value === "removed") {
    void proceedToWorkspace();
  } else {
    reloadTab();
  }
}
</script>

<template>
  <div v-if="visible" class="session-notice" role="alert">
    <div class="session-notice-body">
      <b ref="head" class="session-notice-head" tabindex="-1">{{ title }}</b>
      <p>{{ description }}</p>
      <p v-if="copyMessage" class="session-notice-copy" aria-live="polite">
        {{ copyMessage }}
      </p>
      <textarea
        v-if="fallbackText"
        ref="fallbackArea"
        class="session-notice-fallback"
        readonly
        aria-label="화면 입력 내용"
        :value="fallbackText"
      />
    </div>
    <div class="session-notice-actions">
      <button type="button" class="btn ghost ui-sm" @click="copyFields">
        화면 입력 내용 복사
      </button>
      <button type="button" class="btn primary ui-sm" @click="primaryAction">
        {{ primaryLabel }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* 모달(9999) 위에 둔다 — 확인창이 떠 있어도 왜 요청이 막혔는지 보여야 한다. 토스트(`--ui-z-notice`, 같은 위 가운데 — App.vue)보다도
   한 단계 위다: 오류 토스트가 「다시 로그인」 을 덮으면 안 된다(72차 리뷰). */
.session-notice {
  position: fixed;
  top: 12px;
  left: 50%;
  z-index: calc(var(--ui-z-notice) + 1);
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: min(960px, calc(100vw - 24px));
  padding: 14px 16px;
  color: var(--ui-warn-ink);
  background: var(--ui-warn-bg);
  border: 1px solid var(--ui-warn-line);
  border-radius: var(--ui-radius-control);
  box-shadow: var(--ui-shadow-3);
  transform: translateX(-50%);
}

.session-notice-body {
  flex: 1;
  min-width: 0;
}

.session-notice-head:focus-visible {
  outline: var(--ui-focus-outline);
  outline-offset: 2px;
}

.session-notice-body p {
  margin: var(--ui-gap-2xs) 0 0;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.session-notice-copy {
  font-weight: 700;
}

.session-notice-fallback {
  width: 100%;
  min-height: 120px;
  margin-top: var(--ui-gap-sm);
  font: inherit;
  color: var(--ui-text);
  background: var(--ui-surface);
  border: 1px solid var(--ui-line-strong);
  border-radius: var(--ui-radius-sm);
}

.session-notice-actions {
  display: flex;
  flex-shrink: 0;
  gap: var(--ui-gap-sm);
}

@media (max-width: 760px) {
  .session-notice {
    flex-direction: column;
  }
}
</style>
